import type { ClientError } from '@tribeplatform/gql-client/lib'
import {
  MutationUpdateSpaceContentModuleArgs,
  SpaceContentModule,
} from '@tribeplatform/gql-client/types'
import { SpaceContentModuleFields } from '@tribeplatform/gql-client/types/generated'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getNetworkContentModulesKey } from '../../utils/keys/network.key.js'
import {
  getSpaceContentModulesKey,
  getSpaceKey,
} from '../../utils/keys/space.key.js'

export const useUpdateSpaceContentModule = (options?: {
  fields?: SpaceContentModuleFields
  useMutationOptions?: UseMutationOptions<
    SpaceContentModule,
    ClientError,
    MutationUpdateSpaceContentModuleArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<
    SpaceContentModule,
    ClientError,
    MutationUpdateSpaceContentModuleArgs
  >(
    (input: MutationUpdateSpaceContentModuleArgs) =>
      client.spaces.updateContentModule(input, fields),
    {
      ...useMutationOptions,
      onSuccess: (data, variables, context) => {
        const key = getSpaceContentModulesKey({
          variables: { spaceId: variables.spaceId },
        })
        queryClient.setQueriesData<SpaceContentModule>(key, oldData => ({
          ...oldData,
          ...data,
        }))

        queryClient.invalidateQueries(getNetworkContentModulesKey())

        queryClient.invalidateQueries(
          getSpaceKey({
            variables: {
              id: variables.spaceId,
            },
          }),
        )
        useMutationOptions?.onSuccess?.(data, variables, context)
      },
    },
  )
}
